// Start-timer popup behavior.
//
// Two ways to start in the past: type minutes-ago or type a specific time.
// They're mutually exclusive — typing one clears the other and the submit
// label updates so the user knows what's about to happen:
//
//   blank/blank → "Start now"
//   "10"         → "Start 10 min ago"
//   "4:24pm"     → "Start at 4:24 pm"
//
// Specific-time input is also live-validated; a bad value disables the
// submit and outlines the field.
//
// Mobile: a tap on the icon starts the timer immediately (the link's normal
// behavior). A long-press (~450ms) instead pins the popup open so the user
// can pick a different start time. Tapping anywhere outside dismisses.
import { Controller } from "@hotwired/stimulus"
import { parseTimeOfDay, formatTimeOfDay } from "../lib/time_of_day"

const LONG_PRESS_MS = 450

export default class extends Controller<HTMLElement> {
  static targets = ["minOffset", "asOfTime", "submit"]
  declare readonly minOffsetTarget: HTMLInputElement
  declare readonly asOfTimeTarget: HTMLInputElement
  declare readonly submitTarget: HTMLInputElement | HTMLButtonElement

  private longPressTimer: number | null = null
  private longPressFired = false
  private outsideHandler: ((e: Event) => void) | null = null

  connect(): void {
    this.refresh()
  }

  disconnect(): void {
    this.cancelLongPress()
    this.detachOutside()
  }

  // ---------- Mutually-exclusive offset/time inputs ----------

  minOffsetChanged(): void {
    if (this.minOffsetTarget.value.trim() !== "") this.asOfTimeTarget.value = ""
    this.refresh()
  }

  asOfTimeChanged(): void {
    if (this.asOfTimeTarget.value.trim() !== "") this.minOffsetTarget.value = ""
    this.refresh()
  }

  // ---------- Touch / long-press handling ----------

  touchStart(_event: TouchEvent): void {
    this.longPressFired = false
    this.longPressTimer = window.setTimeout(() => {
      this.longPressFired = true
      this.pin()
    }, LONG_PRESS_MS)
  }

  touchEnd(_event: TouchEvent): void {
    this.cancelLongPress()
  }

  touchCancel(_event: TouchEvent): void {
    this.cancelLongPress()
  }

  // ---------- Desktop pinning ----------
  // The popup is hover-held by CSS, which means it vanishes mid-typing if
  // the cursor drifts away. Focusing any field inside it pins it open
  // (same is-pinned mechanism as mobile long-press); click-outside or
  // Escape unpins. Restores the old desktop "click in to keep it open"
  // behavior that was lost with legacy tasks.ts.
  focusIn(event: FocusEvent): void {
    if ((event.target as HTMLElement).closest(".start_tooltip_form")) this.pin()
  }

  escape(): void {
    this.unpin()
  }

  // Swallow the synthetic click that follows a long-press (we already pinned
  // the popup; we don't want the link to also navigate / start the timer).
  click(event: MouseEvent): void {
    if (this.longPressFired) {
      event.preventDefault()
      event.stopPropagation()
      this.longPressFired = false
    }
  }

  private cancelLongPress(): void {
    if (this.longPressTimer != null) {
      window.clearTimeout(this.longPressTimer)
      this.longPressTimer = null
    }
  }

  // ---------- Pin / unpin (open from long-press) ----------

  private pin(): void {
    this.element.classList.add("is-pinned")
    // Defer attaching the outside handler so the touch that triggered the pin
    // doesn't immediately unpin us.
    setTimeout(() => this.attachOutside(), 0)
  }

  private unpin(): void {
    this.element.classList.remove("is-pinned")
    this.detachOutside()
  }

  private attachOutside(): void {
    if (this.outsideHandler) return
    this.outsideHandler = (e: Event) => {
      const target = e.target as Node | null
      if (target && this.element.contains(target)) return
      this.unpin()
    }
    document.addEventListener("touchstart", this.outsideHandler, { passive: true })
    document.addEventListener("mousedown",  this.outsideHandler)
  }

  private detachOutside(): void {
    if (!this.outsideHandler) return
    document.removeEventListener("touchstart", this.outsideHandler)
    document.removeEventListener("mousedown",  this.outsideHandler)
    this.outsideHandler = null
  }

  // ---------- Validation + submit label ----------

  private refresh(): void {
    const min  = this.minOffsetTarget.value.trim()
    const time = this.asOfTimeTarget.value.trim()

    let label = "Start now"
    let valid = true
    let timeInputInvalid = false

    if (min !== "") {
      const n = parseInt(min, 10)
      if (Number.isFinite(n) && n > 0) {
        label = `Start ${n} min ago`
      } else if (min !== "0") {
        valid = false
        label = "Invalid offset"
      }
    } else if (time !== "") {
      const friendly = formatTimeOfDay(time)
      if (friendly) {
        label = `Start at ${friendly}`
      } else {
        valid = false
        timeInputInvalid = true
        label = "Invalid time"
      }
    }

    this.submitTarget.value = label
    this.submitTarget.disabled = !valid
    this.submitTarget.classList.toggle("opacity-40", !valid)
    this.submitTarget.classList.toggle("cursor-not-allowed", !valid)

    this.asOfTimeTarget.classList.toggle("!border-[var(--color-state-red-500)]", timeInputInvalid)
  }
}
